import type { NewsletterIssueData } from '../types';

/**
 * Shared helpers for the V3.2 newsletter blocks (masthead, issue layout, spotlight card, events
 * list). Pure: no fetching, no framework imports.
 */

/** The issue a block renders: by id, else the newest for its community, else the newest of all. */
export function pickIssue(issues: NewsletterIssueData[] | null | undefined, opts: { issueId?: string | null; communitySlug?: string | null } = {}): NewsletterIssueData | null {
  const list = Array.isArray(issues) ? issues : [];
  if (list.length === 0) return null;
  const id = (opts.issueId ?? '').trim();
  if (id) return list.find((i) => i.id === id) ?? null;
  const slug = (opts.communitySlug ?? '').trim().toLowerCase();
  if (slug) return list.find((i) => i.community_slug === slug) ?? null;
  return list[0] ?? null;
}

export const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "September 2026" from an ISO date; '' when unknown. UTC so the label never shifts by timezone. */
export function monthYear(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Sep 20" (+ ", 1:00 PM" when the time is not midnight UTC) for an event; '' when unknown. */
export function eventDateLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = `${MONTHS[d.getUTCMonth()].slice(0, 3)} ${d.getUTCDate()}`;
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  if (h === 0 && m === 0) return day;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${day}, ${hour12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "Issue No. 12 · September 2026" */
export function issueLabel(issue: Pick<NewsletterIssueData, 'issue_no' | 'published_at'>): string {
  const when = monthYear(issue.published_at);
  return `Issue No. ${issue.issue_no}${when ? ` · ${when}` : ''}`;
}

/** The masthead title: an explicit heading wins, else "The {Community} Letter". */
export function mastheadTitle(heading: string | undefined, communityName: string | undefined | null): string {
  const h = (heading ?? '').trim();
  if (h) return h;
  const c = (communityName ?? '').trim();
  return c ? `The ${c} Letter` : 'The Community Letter';
}

/** Read a localStorage key without throwing (private windows, SSR). */
export function readStorage(key: string): string {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) || '' : '';
  } catch {
    return '';
  }
}

export function writeStorage(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function readBrowserId(): string | undefined {
  try {
    const m = typeof document !== 'undefined' ? document.cookie.match(/(?:^|;\s*)blt_bid=([^;]+)/) : null;
    if (m) return decodeURIComponent(m[1]);
  } catch {
    /* ignore */
  }
  return readStorage('blt_visitor_id') || undefined;
}

/**
 * A URL safe to put in a public `href`: absolute http(s) only, else null so the caller omits the
 * anchor entirely rather than emitting a dead or dangerous one.
 *
 * Shared by SpotlightCard (business website) and EventsList (event link) because both render
 * PERSISTED editor/AI copy on a public tenant page. The gateway validates the scheme on write, but
 * that does not protect rows written before the check existed, nor AI-drafted issue JSON that
 * carries an event link inside `sections` — `rowToEvent` hands the URL back unchanged. So the
 * render keeps its own guard.
 */
export function safeHttpHref(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  // Control characters are stripped by URL parsing but would survive into the attribute, so a
  // value containing any is refused outright rather than silently cleaned.
  if (!value || /[\u0000-\u001f\u007f]/.test(value)) return null;
  try {
    const { protocol } = new URL(value);
    // The original string, not url.toString(): normalising would rewrite the agent's link
    // (a trailing slash appears, escapes change) for no security gain — the scheme is the risk.
    return protocol === 'http:' || protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}
